import { ClientResult } from '@vp/client-result';
import {
  FetchClient,
  AuthTokenHeader,
  ILogger,
  ResponseHandlerBuilder,
  ResponseHandlerCollection,
} from '@vp/fetch-client';
import {
  ProductCompletion,
  ProductEnumerationConfiguration,
  ProductFulfillmentCompletion,
  RanchServiceClient,
} from '@vp/http-clients';

import { ICombination } from '../../types/interfaces';
import { configuration } from '../serverConfigurationProvider';

const ProductIsConstrainedReason = 'productConfigurationIsConstrained';
const StrategiesWithoutRetirement = { '1': 'ProductPreference', '2': 'LowestPrice' };

export async function completeCombination(
  productKey: string,
  version: number,
  merchant: string,
  accountId: string,
  locale: string,
  tokenConfig: AuthTokenHeader,
  client: FetchClient,
  logger: ILogger,
  options?: ICombination,
  signal?: AbortSignal
): Promise<ICombination | null> {
  const ranchServiceClient = new RanchServiceClient(client, configuration.requestor, tokenConfig);

  try {
    const response = await ranchServiceClient.getProductCompletionV1(
      productKey,
      version,
      merchant,
      getMarket(locale),
      accountId,
      options,
      undefined,
      {
        signal,
        responseOverrides: swallowProductIsConstrainedError<ProductCompletion>(),
      }
    );

    if (response.isFailed) {
      return null;
    }

    const defaultConfiguration = response.result.configurations?.[0];
    if (!defaultConfiguration) {
      return null;
    }

    const defaultCombination: ICombination = {};
    defaultConfiguration.options?.forEach((option) => {
      if (option.name != null && option.value != null) {
        defaultCombination[option.name] = option.value;
      }
    });
    return defaultCombination;
  } catch (error) {
    logger.error('Error fetching complete combination:', { error });
  }

  return null;
}

export async function getEnumeration(
  productKey: string,
  version: number,
  merchant: string,
  accountId: string,
  locale: string,
  tokenConfig: AuthTokenHeader,
  client: FetchClient
): Promise<ICombination[]> {
  const ranchServiceClient = new RanchServiceClient(client, configuration.requestor, tokenConfig);
  const configurations: ProductEnumerationConfiguration[] = [];
  let nextBatchToken: string | undefined;

  do {
    const ranchClientResponse = await ranchServiceClient.getProductEnumerationV2(
      productKey,
      version,
      merchant,
      getMarket(locale),
      accountId,
      nextBatchToken
    );

    ranchClientResponse.assertIsNotFailed();

    const pageConfigurations = ranchClientResponse.result?._embedded?.configurations ?? [];

    if (configurations.length === 0 && pageConfigurations.length === 0) {
      throw new Error('Ranch - Enumeration: No configurations found.');
    }

    configurations.push(...pageConfigurations);
    nextBatchToken = ranchClientResponse.result?.nextBatchToken;
  } while (nextBatchToken);

  const combinations = configurations.map((combination) =>
    Object.assign(
      {},
      ...(combination.selectedOptions ?? [])
        .filter((o) => o.name != null && o.value.value != null)
        .map((o) => ({ [o.name!]: o.value.value }))
    )
  );

  return combinations;
}

export async function getFulfillmentCombination(
  productKey: string,
  version: number,
  merchant: string,
  accountId: string,
  locale: string,
  optionSelections: ICombination,
  tokenConfig: AuthTokenHeader,
  client: FetchClient
): Promise<FulfillmentCombination> {
  const ranchServiceClient = new RanchServiceClient(client, configuration.requestor, tokenConfig);

  let ranchClientResponse = await ranchServiceClient.getProductFulfillmentCompletionV1(
    productKey,
    version,
    merchant,
    getMarket(locale),
    optionSelections,
    accountId
  );

  if (allCombinationsConstrained(ranchClientResponse)) {
    ranchClientResponse = await ranchServiceClient.getProductFulfillmentCompletionV1(
      productKey,
      version,
      merchant,
      getMarket(locale),
      optionSelections,
      accountId,
      StrategiesWithoutRetirement,
      {
        responseOverrides: swallowProductIsConstrainedError<ProductFulfillmentCompletion>(),
      }
    );
  }

  ranchClientResponse.assertIsNotFailed();

  if (!ranchClientResponse.result?.configurations?.length) {
    throw new Error('Ranch - Get Fulfillment Combination: No configurations found.');
  }

  const combinations = toCombination(
    ranchClientResponse.result.configurations[0] as FulfillmentConfiguration
  );
  return combinations;
}

function getMarket(locale: string): string {
  return locale.split('-')[1];
}

interface FulfillmentConfiguration {
  productId: string;
  version: number;
  options: Option[];
}

interface Option {
  name: string;
  value: string;
}

function allCombinationsConstrained(
  ranchClientResponse: ClientResult<ProductFulfillmentCompletion>
): boolean {
  const errorDetails = tryParse<{ reason?: unknown }>(ranchClientResponse.errorDetails);
  return (
    ranchClientResponse.statusCode === 400 && errorDetails?.reason === ProductIsConstrainedReason
  );
}

function toCombination(configuration: FulfillmentConfiguration): FulfillmentCombination {
  return {
    productId: configuration.productId,
    version: configuration.version,
    combination: Object.assign(
      {},
      ...(configuration.options ?? []).map((o) => ({ [o.name]: o.value }))
    ),
  };
}

export interface FulfillmentCombination {
  productId: string;
  version: number;
  combination: ICombination;
}

function tryParse<T>(value: unknown): T | null {
  if (typeof value === 'object' && value !== null) {
    return value as T;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null ? (parsed as T) : null;
    } catch {
      return null;
    }
  }

  return null;
}

export function swallowProductIsConstrainedError<
  T extends { configurations?: unknown[] | null | undefined },
>(): ResponseHandlerCollection<T> {
  return ResponseHandlerBuilder.create<T>()
    .with(400, async (_logger, _enricher, _metadata, res) => {
      let body: Record<string, unknown>;
      try {
        body = await res.json();
      } catch (error) {
        return ClientResult.failed(
          {
            response: { status: 400, data: res, error },
            message: 'Failed to parse error response',
          },
          'RanchService'
        );
      }

      if (body.reason === ProductIsConstrainedReason) {
        return Promise.resolve(ClientResult.successful({ configurations: [] } as unknown as T));
      }
      return ClientResult.failed(
        {
          response: { status: 400, data: body },
          message: res.statusText || 'Ranch returned 400 error',
        },
        'RanchService'
      );
    })
    .build();
}
